import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import Formsy from 'formsy-react'

import { Button, IconButton, Table, TableContainer, TableHead, TableBody, Paper, Checkbox, 
         DialogTitle, DialogContent, DialogActions, Tooltip, FormControlLabel } from '@mui/material'
import { Add, Delete, Edit, Refresh, Check, Close } from '@mui/icons-material'

import { D, Loading, SearchField, TRow, TCell, TextFieldFormsy, CustomSelect, ConfirmDelete } from '../include'


// ==========================================
// COMPONENTE: EditPermission (Substitui o NewPermission)
// ==========================================
const EditPermission = ({ row, api, alert, dialogClose, onSuccess, role }) => {
  const isEdit = !!row;
  
  const [category, setCategory] = useState(row?.category || '');
  const [name, setName] = useState(row?.name || '');
  const [description, setDescription] = useState(row?.description || '');
  const [show, setShow] = useState(row?.show !== undefined ? row.show : true);

  const canSave = category.trim() && name && description.trim();

  const handleSubmit = () => {
    const payload = { category, name, description, show };

    const req = isEdit
      ? api.put(`/Permission/${row.id}`, payload)
      : api.post('/Permissions', payload);

    req
      .then(r => {
        alert(r.msg || "Operação realizada com sucesso!");
        onSuccess(); // Fecha o modal e atualiza a tabela pai
      })
      .catch(e => alert(e.msg || "Erro ao salvar permissão."));
  };

  return (
    <Formsy onValidSubmit={handleSubmit}>
      <DialogTitle>{isEdit ? 'Editar permissão' : 'Adicionar permissão'}</DialogTitle>

      <DialogContent>
        <D h="1rem" />
        <TextFieldFormsy 
          required
          value={category} 
          onChange={v => setCategory(v)} 
          type="text" 
          name="category" 
          label="Permissão"  
          disabled={isEdit}
        />
        
        <D h="1rem" />
        <CustomSelect 
          required 
          label="Tipo *" 
          options={['Ler', 'Escrever']} 
          value={name} 
          onChange={v => setName(v)} 
        />
        
        <D h="1rem" />
        <TextFieldFormsy 
          required 
          rows={5} 
          value={description} 
          onChange={v => setDescription(v)} 
          type="text" 
          name="description" 
          label="Descrição"  
        />
        
        {role === 'admin' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={show} 
                onChange={e => setShow(e.target.checked)}
                name="show"
                color="primary"
              />
            }
            label="Visível p/ Todos?"
            style={{ marginTop: '1rem', display: 'block' }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>
          Cancelar
        </Button>
        <Button 
          variant="outlined" 
          color="primary" 
          type="submit"
          disabled={!canSave}
        >
          Salvar
        </Button>
      </DialogActions>
    </Formsy>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: Permissions
// ==========================================
export const Permissions = ({ api, user, permission, dialog, dialogClose, alert }) => {
  const [searchField, setSearchField] = useState('');
  const [dataTable, setDataTable] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Ref para proteger a dupla renderização do Strict Mode
  const isFirstRender = useRef(true);

  const isAdmin = user?.role === 'admin';

  // Usamos useCallback para selar a referência da função na memória
  const updateData = useCallback(() => {
    setIsLoading(true);
    api.get('/Permissions')
      .then(r => {
        setDataTable(Array.isArray(r) ? r : []);
        setIsLoading(false);
      })
      .catch(e => {
        console.error("Erro ao carregar permissões", e);
        setIsLoading(false);
      });
  }, [api]);

  // Efeito blindado
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Tranca a porta instantaneamente
      updateData();
    }
  }, [updateData]);

  const openEditDialog = (row = null) => {
    dialog(
      <div style={{ minWidth: 'min(90vw, 500px)' }}>
        <EditPermission 
          row={row}
          api={api} 
          alert={alert}
          role={user.role}
          dialogClose={dialogClose}
          onSuccess={() => {
            updateData();
            dialogClose();
          }}
        />
      </div>
    );
  };

  const openDeleteDialog = (row) => {
    dialog(
      <ConfirmDelete 
        title="Confirmar exclusão"
        msg="Tem certeza que deseja excluir a permissão:"
        itemName={`${row.id}: ${row.category} - ${row.name}`}
        onCancel={dialogClose}
        onConfirm={() => {
          api.delete(`/Permission/${row.id}`)
            .then(r => {
              alert(r.msg);
              updateData();
              dialogClose();
            })
            .catch(e => alert(e.msg || "Erro ao excluir permissão."));
        }}
      />
    );
  };

  // Filtro de busca super rápido na memória
  const filteredData = useMemo(() => {
    if (!searchField) return dataTable;

    const searchLower = searchField.toLowerCase().replace(/\s/g, '');
    return dataTable.filter(row => {
      const rowValues = Object.values(row).map(v => String(v)).join('');
      return rowValues.toLowerCase().replace(/\s/g, '').includes(searchLower);
    });
  }, [dataTable, searchField]);

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      {permission === 'w' && (
        <>
          <D className="row w aF">
            <SearchField 
              value={searchField} 
              onChange={v => setSearchField(v)} 
            />

            <Tooltip title="Atualizar dados" arrow>
              <IconButton 
                onClick={updateData} 
                style={{ marginLeft: '0.5rem' }}
                color="default"
              >
                <Refresh />
              </IconButton>
            </Tooltip>

            <D e />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => openEditDialog(null)}
            >
              <Add />Permissão
            </Button>
          </D>
          <D h="1rem" />
        </>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow sx={{ height: '3rem', background: '#ccc' }}>
                {permission === 'w' && <TCell align="center" sx={{ width: '8ch', maxWidth: '8ch' }}>Editar</TCell>}
                {permission === 'w' && <TCell align="center" sx={{ width: '8ch', maxWidth: '8ch' }}>Excluir</TCell>}
                
                <TCell sx={{ width: '10ch' }}>ID</TCell>
                <TCell sx={{ width: '30ch' }}>Permissão</TCell>
                
                {isAdmin && <TCell align="center" sx={{ width: '12ch' }}>Todos?</TCell>}
                
                <TCell sx={{ width: '15ch' }}>Tipo</TCell>
                <TCell width="100%">Descrição ({filteredData.length} permissões)</TCell>
              </TRow>
            </TableHead>
            
            <TableBody>
              {isLoading ? (
                <TRow>
                  {/* +5 compensa o ID, Permissão, Tipo, Descrição e o espaço base */}
                  <TCell component="th" align="center" colSpan={(permission === 'w' ? 2 : 0) + (isAdmin ? 1 : 0) + 4}>
                    <Loading />
                  </TCell>
                </TRow>
              ) : filteredData.length > 0 ? (
                filteredData.map(row => (
                  <TRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {permission === 'w' && (
                      <TCell align="center">
                        <IconButton
                          style={{ color: '#aaa' }}
                          onClick={() => openEditDialog(row)}
                        >
                          <Edit />
                        </IconButton>
                      </TCell>
                    )}

                    {permission === 'w' && (
                      <TCell align="center">
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openDeleteDialog(row)}
                        >
                          <Delete />
                        </IconButton>
                      </TCell>
                    )}
                    
                    <TCell component="th">{row.id}</TCell>
                    <TCell component="th">{row.category}</TCell>
                    
                    {isAdmin && (
                      <TCell align="center" component="th">
                        {row.show ? <Check color="success" /> : <Close color="error" />}
                      </TCell>
                    )}
                    
                    <TCell component="th">{row.name}</TCell>
                    <TCell component="th" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                      {row.description}
                    </TCell>
                  </TRow>
                ))
              ) : (
                <TRow>
                  <TCell component="th" align="center" colSpan={(permission === 'w' ? 2 : 0) + (isAdmin ? 1 : 0) + 4}>
                    Nenhuma permissão encontrada
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};